/*
 * ContactForm.tsx : Un formulaire pour ajouter ou modifier un contact.
 */

// Import React
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserTie } from '@fortawesome/free-solid-svg-icons';

// Import Communs
import {
	CheckboxField,
	finishFormSave,
	FormSaveActions,
	InputField,
	ToggleButtonField,
	useFormDirty,
} from '../../../common';

// Import Contact
import { ContactData } from '../types/types';
import { contactsApi } from '../services/contactsApi';

// Composant ContactForm
const ContactForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [formData, setFormData] = React.useState<ContactData>({
		id: 0,
		firstName: '',
		lastName: '',
		workPhone: '',
		mobilePhone: '',
		email: '',
		position: '',
		businessManager: false,
		status: true,
		createdAt: new Date(),
		tiers: { id: 0, label: '' },
	});

	// Hook de Navigation et Paramètres de navigation
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const tiersIdParam = searchParams.get('tiersId') || '';
	const tiersLabelParam = searchParams.get('tiersLabel') || '';
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Une fois le composant monté et si un id est présent, fetch du contact et préremplissage du formulaire
	React.useEffect(() => {
		if (id) {
			const token = localStorage.getItem('token');
			if (!token) return;

			contactsApi
				.get(Number(id), token)
				.then((data) => setFormData(data))
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on préremplit le tiers si présent dans les paramètres
			if (tiersIdParam) {
				const tiersId = Number(tiersIdParam) || 0;
				const tiersLabel = tiersLabelParam || '';
				setFormData({
					...formData,
					tiers: { id: tiersId, label: tiersLabel },
				});
			}
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Gestion des changements dans le formulaire
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () =>
			formData.tiers?.id
				? navigate(`/app/tiers/${formData.tiers.id}/manage`)
				: navigate('/app/contacts');
		// Si un id est présent, mise à jour du contact
		if (id) {
			contactsApi
				.update(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouveau contact
			contactsApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit:
							!id && created?.id
								? () =>
										navigate(`/app/contacts/${created.id}/edit`, {
											replace: true,
										})
								: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<div className="flex items-start mb-3 space-x-4">
					{/* Icône à gauche */}
					<FontAwesomeIcon
						icon={faUserTie}
						className="w-20 h-20 text-blue-900/90"
					/>

					{/* Titre + toggle à droite */}
					<div className="flex flex-col mt-3">
						<h1 className="text-3xl font-bold">
							{id ? <>Édition d&apos;un contact</> : <>Nouveau contact</>}
						</h1>

						{/* Toggle “Actif” sous le titre */}
						<div className="mt-2">
							<ToggleButtonField
								label="Actif"
								isOn={formData.status ?? true}
								onChange={(value) =>
									setFormData({ ...formData, status: value })
								}
								invert={true}
								size="sm"
								loading={loading}
							/>
						</div>
					</div>
				</div>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-16">
							<div className="space-y-4">
								<InputField
									label="Prénom"
									name="firstName"
									type="text"
									value={formData.firstName}
									onChange={handleChange}
									placeholder="Jean"
									required
									loading={loading}
								/>
								<InputField
									label="Nom"
									name="lastName"
									type="text"
									value={formData.lastName}
									onChange={handleChange}
									placeholder="Dupont"
									required
									loading={loading}
								/>
								{/* Input pour l'ID du tiers associé. Il n'y en a qu'un seul dans tiers */}
								<InputField
									label="Tiers associé (ID)"
									name="tiersId"
									value={
										formData.tiers
											? String(formData.tiers.id)
											: 'tiers non associé'
									}
									onChange={(e) => {
										const id = Number(e.target.value) || 0;
										const newTiers =
											formData.tiers && formData.tiers.id
												? { ...formData.tiers }
												: { id, label: '' };
										setFormData({ ...formData, tiers: newTiers });
									}}
									readOnly={true}
									visibility={false}
									loading={loading}
								/>
								{/* Input pour le label du tiers associé. Il n'y en a qu'un seul dans tiers. N'est pas modifiable */}
								<InputField
									label="Label du tiers associé"
									name="tiersLabel"
									value={formData.tiers ? formData.tiers.label : ''}
									onChange={(e) => {
										const label = e.target.value;
										const newTiers = formData.tiers
											? { ...formData.tiers, label }
											: { id: 0, label };
										setFormData({ ...formData, tiers: newTiers });
									}}
									readOnly={true}
									loading={loading}
								/>
								<CheckboxField
									label="Gestionnaire d'affaires"
									checked={formData.businessManager || false}
									onChange={(checked) =>
										setFormData({ ...formData, businessManager: checked })
									}
									size="md"
									loading={loading}
								/>
							</div>

							<div className="space-y-4">
								<InputField
									label="Téléphone professionnel"
									name="workPhone"
									type="text"
									value={formData.workPhone}
									onChange={handleChange}
									placeholder="+33 1 23 45 67 89"
									required
									loading={loading}
								/>
								<InputField
									label="Téléphone mobile"
									name="mobilePhone"
									type="text"
									value={formData.mobilePhone}
									onChange={handleChange}
									placeholder="+33 6 12 34 56 78"
									required
									loading={loading}
								/>
								<InputField
									label="Email"
									name="email"
									type="email"
									value={formData.email}
									onChange={handleChange}
									placeholder="jean.dupont@example.com"
									required
									loading={loading}
								/>
								<InputField
									label="Poste"
									name="position"
									type="text"
									value={formData.position}
									onChange={handleChange}
									placeholder="Directeur Commercial"
									required
									loading={loading}
								/>
							</div>
						</div>
					</form>
					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() =>
							navigate(
								formData.tiers?.id
									? `/app/tiers/${formData.tiers?.id}/manage`
									: '/app/contacts'
							)
						}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default ContactForm;
